// Funções puras do "Histórico de cliente" (extraídas de HistoricoCliente.vue).
// Formatadores, normalizadores de payload e mapeadores de label/tom usados tanto
// pela página quanto pelos componentes/composables da rota. Sem estado reativo.
import { LEAD_HISTORY_LOAD_STATES } from 'dashboard/api/leadHistory';
import {
  ATTENDANCE_WINDOW_REASON_LABEL,
  SUPPORT_ORIGIN_LABEL,
  SUPPORT_QUESTION_TYPE_LABEL,
  SUPPORT_STATUS_META,
  TIMELINE_TYPE_META,
  UNAVAILABLE_LOAD_STATES,
} from './leadHistoryConstants';

export const routeString = value => (Array.isArray(value) ? value[0] : value);

export const toDate = value => {
  if (!value) return null;

  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return new Date(numeric < 1_000_000_000_000 ? numeric * 1000 : numeric);
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatDateTime = value => {
  const date = toDate(value);
  return date ? date.toLocaleString('pt-BR') : '—';
};

export const formatDateCompact = value => {
  const date = toDate(value);
  return date ? date.toLocaleDateString('pt-BR') : '';
};

export const formatCurrency = value => {
  if (value == null || value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return numeric.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
};

export const compactList = value => {
  if (Array.isArray(value)) {
    return value
      .map(String)
      .map(v => v.trim())
      .filter(Boolean);
  }
  if (value && typeof value === 'object') {
    return Object.values(value)
      .flatMap(item => compactList(item))
      .filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(/[,;|]/)
      .map(item => item.trim())
      .filter(Boolean);
  }
  return value ? [String(value)] : [];
};

export const formatVisitWindow = value => {
  if (!value) return '—';
  if (Array.isArray(value)) return value.map(formatVisitWindow).join(', ');
  if (typeof value === 'object') {
    return (
      value.label ||
      value.description ||
      [
        value.date,
        value.start_time || value.starts_at,
        value.end_time || value.ends_at,
      ]
        .filter(Boolean)
        .join(' ')
    );
  }
  return String(value);
};

export const propertyTitle = imovel => {
  if (imovel?.titulo) return imovel.titulo;
  if (imovel?.id) return `Imóvel #${imovel.id}`;
  return 'Imóvel não informado';
};

export const propertyLocation = imovel =>
  [imovel?.bairro, imovel?.cidade].filter(Boolean).join(' · ') ||
  'Não informado';

export const propertyBudget = imovel =>
  formatCurrency(imovel?.valor_venda || imovel?.valor_aluguel) ||
  'Valor não informado';

export const noteAuthor = note => note.user?.name || note.userName || 'Lais';

export const qualificationAnswerText = question => {
  const answer = question.answer || {};
  const rawValue = answer.answer_text || answer.text || answer.answer_value;
  const value = rawValue?.value ?? rawValue;
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  if (value && typeof value === 'object') {
    return Object.values(value).filter(Boolean).join(', ');
  }
  return value || '';
};

export const qualificationQuestionsStatusFromLead = lead =>
  lead?.qualification_questions ||
  lead?.lais_crm?.qualification_questions ||
  {};

export const qualificationQuestionItemsFromLead = lead =>
  qualificationQuestionsStatusFromLead(lead).questions || [];

export const collectionFromResponse = data => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.payload)) return data.payload;
  if (Array.isArray(data?.data?.payload)) return data.data.payload;
  return [];
};

export const hasHistoryPayloadKey = (payload, key) =>
  Object.prototype.hasOwnProperty.call(payload, key);

export const shouldApplyHistoryValue = (payload, key, preserveMissing) =>
  !preserveMissing || hasHistoryPayloadKey(payload, key);

export const arrayPayloadValue = value => (Array.isArray(value) ? value : []);

export const pendingSupportPayloadFromResponse = data =>
  data?.payload || data?.data?.payload || null;

export const isUnavailableLoadState = state =>
  UNAVAILABLE_LOAD_STATES.includes(state);

export const loadStateCopy = (copyMap, state) =>
  copyMap[state] || copyMap[LEAD_HISTORY_LOAD_STATES.ERROR];

export const supportStatus = support =>
  support?.duty?.status ||
  support?.status_label ||
  support?.status ||
  'pending';

export const supportStatusMeta = support =>
  SUPPORT_STATUS_META[supportStatus(support)] || {
    label: supportStatus(support),
    tone: 'bg-[#f5f5f5] text-lais-text-body',
    nextAction: 'Revisar o atendimento de suporte.',
  };

export const nextSupportStatusFor = support => {
  const status = supportStatus(support);
  if (status === 'pending') return 'in_progress';
  if (status === 'in_progress') return 'complete';
  return 'complete';
};

export const normalizeSupportOrigin = support => {
  const origin = support?.duty?.support_origin;
  if (origin) return SUPPORT_ORIGIN_LABEL[origin] || origin;

  return support?.inbox?.name || 'Origem não informada';
};

export const supportNextActionLabel = (support, canUpdate) => {
  if (!support) return '';
  if (!canUpdate) {
    return 'Solicitar a um responsável autorizado a atualização do plantão.';
  }

  return supportStatusMeta(support).nextAction;
};

export const normalizeSupportQuestion = support => {
  const question = support?.duty?.support_question;
  if (question?.question) return question.question;

  const questionType = support?.duty?.question_type;
  return SUPPORT_QUESTION_TYPE_LABEL[questionType] || 'Pergunta não informada';
};

export const attendanceWindowStatusLabel = window => {
  if (!window) return 'Sem janela registrada';
  if (window.meterable === false) return 'Sem cobrança';
  return window.status === 'open' ? 'Janela ativa' : 'Janela encerrada';
};

export const attendanceWindowReasonLabel = window => {
  const reason = window?.invalid_reason;
  return ATTENDANCE_WINDOW_REASON_LABEL[reason] || reason;
};

export const timelineEventMeta = event => TIMELINE_TYPE_META[event.type] || {};

export const timelineEventIcon = event =>
  timelineEventMeta(event).icon || 'i-lucide-activity';

export const timelineEventTone = event =>
  timelineEventMeta(event).tone || 'bg-[#f8f4fc] text-lais-primary';

export const timelineEventStatus = event =>
  event.metadata?.status_label ||
  event.metadata?.status ||
  event.metadata?.severity ||
  '';

export const timelineEventChips = event =>
  [
    timelineEventMeta(event).label,
    timelineEventStatus(event),
    event.metadata?.agent?.name ? `Dono: ${event.metadata.agent.name}` : null,
    event.metadata?.assignee?.name
      ? `Responsável: ${event.metadata.assignee.name}`
      : null,
    event.metadata?.from_agent?.name
      ? `De: ${event.metadata.from_agent.name}`
      : null,
    event.metadata?.to_agent?.name
      ? `Para: ${event.metadata.to_agent.name}`
      : null,
    event.metadata?.due_at
      ? `Prazo: ${formatDateTime(event.metadata.due_at)}`
      : null,
  ].filter(Boolean);

export const blockedContactPayload = record => ({
  id: record.id,
  motivo: record.motivo,
  observacoes: record.observacoes,
  source: record.source,
  source_label: record.source_label,
  expires_at: record.expires_at,
  expiration_status: record.expiration_status,
  blocked_by_user_id: record.blocked_by_user_id,
  blocked_by_user_name: record.blocked_by_user_name,
  created_at: record.created_at,
  updated_at: record.updated_at,
});

export const compactQuery = query =>
  Object.fromEntries(
    Object.entries(query).filter(([, value]) => value != null && value !== '')
  );

export const querySignature = query =>
  Object.entries(query)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${Array.isArray(value) ? value[0] : value}`)
    .join('|');

export const leadAttributeValue = (lead, paths) => {
  const sources = [
    lead?.additional_attributes || {},
    lead?.custom_attributes || {},
    lead || {},
  ];

  return (
    sources.reduce((found, source) => {
      if (found != null && found !== '') return found;

      return paths.reduce((pathFound, path) => {
        if (pathFound != null && pathFound !== '') return pathFound;

        const keys = Array.isArray(path) ? path : [path];
        const value = keys.reduce((carry, key) => carry?.[key], source);
        return value != null && value !== '' ? value : pathFound;
      }, null);
    }, null) || null
  );
};

export const buildOperationAreaGroups = lead => {
  const groups = [
    {
      label: 'Cidades',
      values: compactList(
        leadAttributeValue(lead, [
          'cidades_interesse',
          'cidade_interesse',
          'cidades',
          'cidade',
          'city',
          ['address', 'city'],
        ])
      ),
    },
    {
      label: 'Bairros',
      values: compactList(
        leadAttributeValue(lead, [
          'bairros_interesse',
          'bairro_interesse',
          'bairros',
          'bairro',
          'neighborhoods',
          'neighborhood',
          ['address', 'neighborhood'],
        ])
      ),
    },
    {
      label: 'Regiões',
      values: compactList(
        leadAttributeValue(lead, [
          'regioes_interesse',
          'regiao_interesse',
          'areas_atuacao',
          'areas_de_atuacao',
          'operation_areas',
        ])
      ),
    },
  ].filter(group => group.values.length);

  if (!groups.length && lead?.location) {
    groups.push({
      label: 'Localização informada',
      values: compactList(lead.location),
    });
  }

  return groups;
};
